import type { ReactNode } from "react";

import type { EventCardTarget } from "components/EventSummaryCard";
import { cn } from "utils";

import { eventPhase, phaseLabel } from "./event-dates";
import {
  DraftChip,
  EventCardShell,
  EventMetaLine,
  type EventCardEvent,
} from "./EventTileParts";

/**
 * The next event, large: its image under a dark overlay (the Dusk live gradient
 * when there is none), a status pill, the title set big, then dates and place.
 *
 * The text sits on the overlay in white, not on the picture, so a bright photo
 * cannot take the title's contrast with it. The card's height is fixed by
 * `min-h`, and the image is absolutely positioned inside it, so nothing moves
 * while the image loads.
 */
export function EventHeroCard({
  event,
  target,
  today,
  badges,
  note,
  unpublished = false,
  testId = "event-card",
}: {
  event: EventCardEvent;
  target?: EventCardTarget | null;
  /** Today's `yyyy-mm-dd`, which the status pill is counted from. */
  today: string;
  /** Markers such as Registered and Admin, beside the status pill. */
  badges?: ReactNode;
  /** A short line under the details, for why the card leads nowhere. */
  note?: ReactNode;
  unpublished?: boolean;
  testId?: string;
}) {
  const phase = eventPhase(event.startDate, event.endDate, today);
  const status = phaseLabel(phase);
  // The hard edge is for the event that is running, or starts today. A later one
  // is quiet in light and keeps the lit edge in Dusk.
  const running =
    phase.kind === "live" || (phase.kind === "upcoming" && phase.days === 0);

  return (
    <EventCardShell
      target={target}
      testId={testId}
      variant="hero"
      unpublished={unpublished}
      // A plain string, not `cn`: tailwind-merge would drop `border-edge`.
      className={`group relative isolate flex min-h-[26rem] flex-col justify-end overflow-hidden rounded-[1.75rem] bg-dusk-live p-5 text-white sm:min-h-[28rem] sm:p-8 lg:min-h-[30rem] ${
        running
          ? "border-edge border-lit-edge shadow-lit-strong"
          : "border border-border shadow-card dark:border-edge dark:border-lit-edge dark:shadow-lit-strong"
      }`}
    >
      {event.image && (
        <img
          src={event.image}
          alt=""
          className={cn(
            "absolute inset-0 -z-20 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105",
            unpublished && "opacity-60 grayscale",
          )}
        />
      )}
      <div
        data-testid="event-hero-overlay"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-hb-warm-grey/10 from-30% to-hb-warm-grey/90"
      />

      <div className="flex min-w-0 flex-col items-start gap-3">
        <h3 className="font-display text-4xl font-normal leading-[0.95] tracking-tight wrap-anywhere sm:text-5xl lg:text-6xl">
          {event.title}
        </h3>

        <EventMetaLine
          event={event}
          today={today}
          className="text-base text-white"
        />
        {note && (
          <p
            className="max-w-prose text-sm font-medium text-white"
            data-testid={`${testId}-note`}
          >
            {note}
          </p>
        )}

        {/* Last in the DOM, so a reader hears the title first, and first on
            screen through `order`. */}
        <div className="order-first flex flex-wrap items-center gap-2">
          {status && (
            <span className="inline-flex items-center gap-2 rounded-full border border-white/45 bg-white/15 px-3.5 py-1.5 text-sm text-white">
              <span
                className="h-2 w-2 rounded-full bg-hb-yellow shadow-lit-dot"
                aria-hidden="true"
              />
              {status}
            </span>
          )}
          {unpublished && <DraftChip />}
          {badges}
        </div>
      </div>
    </EventCardShell>
  );
}
